"use client";
import { useMutation } from "@apollo/client";
import { Image, Modal } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import html2canvas from "html2canvas";

import { useSeasonTheme } from "@/components/model/seasonDecoration";
import { Button, Group, XIcon } from "@/components/ui/elements";

import type { SeasonTheme } from "@/libs/seasonDecoration";
import { buildXShareIntentUrl } from "@/libs/share";

import classes from "./_css/PayrollConfirmModal.module.css";
import { uploadSnapshotImageMutation } from "./_graphql";

type FallingItemKind =
  | "pumpkin"
  | "ghost"
  | "sparkle"
  | "tree"
  | "gift"
  | "snow";

type FallingItem = {
  icon: string;
  kind: FallingItemKind;
  left: string;
  size: string;
  duration: string;
  delay: string;
  sway: number;
};

const HALLOWEEN_FALLING_ITEMS: readonly FallingItem[] = [
  {
    icon: "✨",
    kind: "sparkle",
    left: "3%",
    size: "0.8rem",
    duration: "4.2s",
    delay: "-1.1s",
    sway: 9,
  },
  {
    icon: "🎃",
    kind: "pumpkin",
    left: "11%",
    size: "1.6rem",
    duration: "6.4s",
    delay: "-3.8s",
    sway: -14,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "19%",
    size: "0.8rem",
    duration: "4.3s",
    delay: "-0.6s",
    sway: 12,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "27%",
    size: "0.7rem",
    duration: "4.6s",
    delay: "-2.9s",
    sway: -8,
  },
  {
    icon: "👻",
    kind: "ghost",
    left: "35%",
    size: "1.4rem",
    duration: "7s",
    delay: "-1.7s",
    sway: 15,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "43%",
    size: "0.9rem",
    duration: "4s",
    delay: "-3.4s",
    sway: -10,
  },
  {
    icon: "🎃",
    kind: "pumpkin",
    left: "51%",
    size: "1.5rem",
    duration: "6.8s",
    delay: "-0.9s",
    sway: 13,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "59%",
    size: "0.85rem",
    duration: "4.7s",
    delay: "-4.2s",
    sway: -16,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "67%",
    size: "0.8rem",
    duration: "4.9s",
    delay: "-1.4s",
    sway: 11,
  },
  {
    icon: "👻",
    kind: "ghost",
    left: "75%",
    size: "1.3rem",
    duration: "7.2s",
    delay: "-3s",
    sway: -12,
  },
  {
    icon: "🎃",
    kind: "pumpkin",
    left: "83%",
    size: "1.4rem",
    duration: "6.1s",
    delay: "-0.4s",
    sway: 8,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "90%",
    size: "0.7rem",
    duration: "4.1s",
    delay: "-2.2s",
    sway: -9,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "97%",
    size: "0.75rem",
    duration: "4.4s",
    delay: "-2.6s",
    sway: 7,
  },
];

// クリスマスはハロウィンと同じ位置・速さ・揺れ幅で、モチーフだけを雪・ツリー・プレゼントに替える
const CHRISTMAS_FALLING_ITEMS: readonly FallingItem[] = [
  {
    icon: "✨",
    kind: "sparkle",
    left: "3%",
    size: "0.8rem",
    duration: "4.2s",
    delay: "-1.1s",
    sway: 9,
  },
  {
    icon: "🎄",
    kind: "tree",
    left: "11%",
    size: "1.6rem",
    duration: "6.4s",
    delay: "-3.8s",
    sway: -14,
  },
  {
    icon: "❄️",
    kind: "snow",
    left: "19%",
    size: "0.8rem",
    duration: "4.3s",
    delay: "-0.6s",
    sway: 12,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "27%",
    size: "0.7rem",
    duration: "4.6s",
    delay: "-2.9s",
    sway: -8,
  },
  {
    icon: "🎁",
    kind: "gift",
    left: "35%",
    size: "1.4rem",
    duration: "7s",
    delay: "-1.7s",
    sway: 15,
  },
  {
    icon: "❄️",
    kind: "snow",
    left: "43%",
    size: "0.9rem",
    duration: "4s",
    delay: "-3.4s",
    sway: -10,
  },
  {
    icon: "🎄",
    kind: "tree",
    left: "51%",
    size: "1.5rem",
    duration: "6.8s",
    delay: "-0.9s",
    sway: 13,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "59%",
    size: "0.85rem",
    duration: "4.7s",
    delay: "-4.2s",
    sway: -16,
  },
  {
    icon: "❄️",
    kind: "snow",
    left: "67%",
    size: "0.8rem",
    duration: "4.9s",
    delay: "-1.4s",
    sway: 11,
  },
  {
    icon: "🎁",
    kind: "gift",
    left: "75%",
    size: "1.3rem",
    duration: "7.2s",
    delay: "-3s",
    sway: -12,
  },
  {
    icon: "🎄",
    kind: "tree",
    left: "83%",
    size: "1.4rem",
    duration: "6.1s",
    delay: "-0.4s",
    sway: 8,
  },
  {
    icon: "❄️",
    kind: "snow",
    left: "90%",
    size: "0.7rem",
    duration: "4.1s",
    delay: "-2.2s",
    sway: -9,
  },
  {
    icon: "✨",
    kind: "sparkle",
    left: "97%",
    size: "0.75rem",
    duration: "4.4s",
    delay: "-2.6s",
    sway: 7,
  },
];

const FALLING_ITEMS: Record<SeasonTheme, readonly FallingItem[]> = {
  halloween: HALLOWEEN_FALLING_ITEMS,
  christmas: CHRISTMAS_FALLING_ITEMS,
};

const FALLING_ITEM_CLASS_NAME: Record<FallingItemKind, string> = {
  pumpkin: classes.fallItemPumpkin,
  ghost: classes.fallItemGhost,
  sparkle: classes.fallItemSparkle,
  tree: classes.fallItemTree,
  gift: classes.fallItemGift,
  snow: classes.fallItemSnow,
};

// 背景のグラデーション（￥表記・情報構造は変えず、背景色のみ変える）
const MODAL_BODY_CLASS_NAME: Record<SeasonTheme, string> = {
  halloween: classes.modalBodyHalloween,
  christmas: classes.modalBodyChristmas,
};

const OK_BUTTON_COLOR: Record<SeasonTheme, string> = {
  halloween: "#F39800",
  christmas: "#1F7A50",
};

type Props = {
  finalPaymentAmount: number;
  handleSubmit: () => void;
  opened: boolean;
  keySuffix: string;
  zIndexOffset: number;
  // SNS 共有用の参照トークン。完了カード画像がまだ無い勤務（休業補償・作成失敗・修正依頼の承認直後で作成中）は null で、共有ボタンを出さない
  shareToken?: string | null;
};

// 最大幅と最大高さ、フッターの高さを定数として定義
const MAX_WIDTH = 391;
const MAX_HEIGHT = 845;
const FOOTER_HEIGHT = 70;

export function PayrollConfirmModal({
  finalPaymentAmount,
  handleSubmit,
  opened,
  keySuffix,
  zIndexOffset,
  shareToken,
}: Props) {
  const { height, width } = useViewportSize();
  const [mutation, { loading }] = useMutation(uploadSnapshotImageMutation);
  const modalId = `payroll-confirm-modal-${keySuffix}`;
  const footerId = `payroll-confirm-footer-${keySuffix}`;
  const handleTweetX = async () => {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modalCanvas = await html2canvas(modalElement, {
        width: MAX_WIDTH - 1,
        height: MAX_HEIGHT - FOOTER_HEIGHT - 1,
        scale: 1,
      });
      const modalUrl = modalCanvas.toDataURL("image/png");
      const twitterXPostUrl =
        "https://twitter.com/intent/tweet?text=%E5%A0%B1%E9%85%AC%E3%81%8C%E6%8C%AF%E3%82%8A%E8%BE%BC%E3%81%BE%E3%82%8C%E3%81%BE%E3%81%97%E3%81%9F%EF%BC%81&url=";
      await mutation({
        variables: {
          input: {
            uploadImage: modalUrl,
          },
        },
        onError: (error) => {},
        onCompleted: (data) => {
          const imageUrl = data.uploadSnapshotImage?.imageUrl;
          imageUrl && window.open(twitterXPostUrl + imageUrl);
        },
      });
    }
  };
  const xShareUrl = shareToken
    ? buildXShareIntentUrl(process.env.NEXT_PUBLIC_BASE_URL, shareToken)
    : null;
  const isFullscreen = width < MAX_WIDTH || height < MAX_HEIGHT;
  const seasonTheme = useSeasonTheme();
  const modalBodyClassName = [
    isFullscreen ? classes.modalBodyFull : classes.modalBody,
    seasonTheme ? MODAL_BODY_CLASS_NAME[seasonTheme] : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Modal
      withCloseButton={false}
      onClose={() => {}}
      classNames={{
        root: classes.root,
        content: classes.modalContent,
      }}
      styles={{
        body: {
          padding: 0,
          height: "100%",
          maxHeight: MAX_HEIGHT,
          display: "flex",
          flexDirection: "column",
        },
      }}
      opened={opened}
      zIndex={300 + zIndexOffset}
      size={MAX_WIDTH}
      mah={MAX_HEIGHT}
      fullScreen={isFullscreen} // iphone 12pro以下は全画面
      centered
    >
      <div
        className={modalBodyClassName}
        id={modalId}
        style={{ flex: 1, overflow: "auto" }}
      >
        {seasonTheme && (
          <div className={classes.seasonFallingLayer} aria-hidden="true">
            {FALLING_ITEMS[seasonTheme].map((item, index) => (
              <span
                key={index}
                className={classes.fallItem}
                style={
                  {
                    left: item.left,
                    "--duration": item.duration,
                    "--delay": item.delay,
                  } as React.CSSProperties
                }
              >
                <span
                  className={`${classes.fallItemInner} ${FALLING_ITEM_CLASS_NAME[item.kind]}`}
                  style={
                    {
                      fontSize: item.size,
                      "--duration": item.duration,
                      "--delay": item.delay,
                      "--sway": item.sway,
                    } as React.CSSProperties
                  }
                >
                  {item.icon}
                </span>
              </span>
            ))}
          </div>
        )}
        <div className={classes.logo}>
          <Image
            src={"/images/confirm_modal/logo.png"}
            alt={"ケアリンク"}
            w={312}
            h={102}
            fit="contain"
          />
        </div>

        <div className={classes.main}>
          <div className={classes.money}>
            <p className={classes.text}>報酬が確定しました！</p>
            <Image
              src={"/images/confirm_modal/money.png"}
              alt={"報酬"}
              fit="contain"
              w={156}
              h={192}
            />
          </div>

          <p className={classes.amountText}>
            ￥{finalPaymentAmount?.toLocaleString()}
          </p>
        </div>
        {xShareUrl && (
          // リンクとして新しいタブで開くため、ポップアップブロックの対象にならない
          <Button
            component="a"
            href={xShareUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            pos="absolute"
            left={15}
            bottom={15}
            color="black"
            leftSection={<XIcon />}
            // 報酬額などの表示領域（z-index: 1）が全面に重なっているため、その上に出して押せるようにする
            style={{ zIndex: 2 }}
          >
            Xでシェア
          </Button>
        )}
        {/* <Button
          size="sm"
          pos="absolute"
          left={15}
          bottom={15}
          onClick={handleTweetX}
          color="black"
          leftSection={<XIcon />}
          loading={loading}
        >
          シェアする
        </Button> */}
      </div>
      <Group justify="center" h={FOOTER_HEIGHT} bg="white" id={footerId}>
        <Button
          color={seasonTheme ? OK_BUTTON_COLOR[seasonTheme] : "cyan"}
          onClick={handleSubmit}
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "center",
          }}
        >
          OK
        </Button>
      </Group>
    </Modal>
  );
}
